import { MessageCircle, Phone, Mail, MapPin, Globe, Facebook, Instagram } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import { PageHeader } from "../components/Detail";
import { waLink, PHONE_DISPLAY, EMAIL, ADDRESS } from "../lib/site";

export default function Contact() {
  const { t } = useLanguage();
  const c = t.contact;
  return (
    <main id="main-content">
      <PageHeader overline={c.pageOverline} title={c.pageTitle} sub={c.pageSub} overlineTestId="contact-page-overline" />
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-12">
        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal>
            <article className="flex h-full flex-col rounded-3xl bg-teal p-8 sm:p-10" data-testid="contact-whatsapp-card">
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">WhatsApp</p>
              <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">{c.whatsappTitle}</h2>
              <p className="mt-4 max-w-lg leading-relaxed text-white/85">{c.whatsappText}</p>
              <div className="mt-6 rounded-2xl border border-white/25 bg-white/10 p-5">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">{c.whatsappPreviewLabel}</p>
                <p className="mt-2 text-sm italic leading-relaxed text-white" data-testid="contact-whatsapp-preview">
                  &bdquo;{t.wa.message}&ldquo;
                </p>
              </div>
              <a
                href={waLink(t.wa.message)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.common.whatsappAria}
                data-testid="contact-whatsapp-cta"
                className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-petrol transition-colors hover:bg-gold"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                {t.common.whatsappCtaLong}
              </a>
              <p className="mt-4 text-sm text-white/70">{PHONE_DISPLAY}</p>
            </article>
          </Reveal>

          <div className="grid gap-4">
            <Reveal delay={0.05}>
              <article className="rounded-3xl border border-warmline bg-white p-7" data-testid="contact-details-card">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-light text-teal-dark">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">{c.phoneTitle}</p>
                      <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} data-testid="contact-phone" className="mt-1 block font-semibold text-petrol hover:text-teal-dark">
                        {PHONE_DISPLAY}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-light text-teal-dark">
                      <Mail className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">{c.emailTitle}</p>
                      <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="mt-1 block font-semibold text-petrol hover:text-teal-dark">
                        {EMAIL}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-light text-teal-dark">
                      <MapPin className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">{c.addressTitle}</p>
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=Traute-Lafrenz-Stra%C3%9Fe+161%2C+22297+Hamburg"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-address"
                        className="mt-1 block font-semibold text-petrol hover:text-teal-dark"
                      >
                        {ADDRESS}
                      </a>
                      <p className="mt-1 text-sm text-slate2">{c.addressNote}</p>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>

            <Reveal delay={0.08}>
              <article className="rounded-3xl border border-warmline bg-white p-7" data-testid="contact-online-card">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-light text-teal-dark">
                    <Globe className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-semibold tracking-tight text-petrol">{c.onlineTitle}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate2">{c.onlineText}</p>
                  </div>
                </div>
              </article>
            </Reveal>

            <Reveal delay={0.11}>
              <article className="rounded-3xl border border-warmline bg-white p-7" data-testid="contact-social-card">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-dark">{c.socialTitle}</p>
                <div className="mt-4 flex gap-3">
                  <a
                    href="https://www.facebook.com/HypnosePraxisHamburg"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    data-testid="contact-social-facebook"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
                  >
                    <Facebook className="h-4 w-4" aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.instagram.com/hypnosepraxishamburg"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    data-testid="contact-social-instagram"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-light text-teal-dark transition-colors hover:bg-teal hover:text-white"
                  >
                    <Instagram className="h-4 w-4" aria-hidden="true" />
                  </a>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </div>
    </main>
  );
}
